import { formatText, getPageUrl, getResultLanguageURI } from '@/utils/helpers';
import { parseStringBool } from '@/utils/parse';
import { resolveDate } from '@/utils/resolvers';
import {
  concat,
  cond,
  first,
  get,
  identity,
  isArray,
  isNil,
  keys,
  merge,
  omitBy,
  pipe,
  reduce,
  T,
} from 'lodash/fp';
import qs from 'query-string';
import { resolveCategories, resolveESArray } from '../FilteredList/Results/Results.utils';
import ElasticAdapter from '../shared/Adapters/ElasticAdapter';

// TODO: migrate to ContentType approach.
export const parseUrl = (
  { url, tax_content_category, id, language }: any,
  config: Record<string, string>,
) => {
  if (url?.raw) {
    return getResultLanguageURI({ url, language }, config?.language);
  }

  if (tax_content_category?.raw?.length) {
    const rawUrl = formatText(config?.sdl_detail_url || '/supported-device-list?id=%1', [
      id?.raw || '',
    ]);
    return qs.stringifyUrl({ url: rawUrl, query: {} });
  }

  return '';
};

export const adaptResults = ({
  results,
  page,
  config,
  contentCategoryCardFieldMapper = {},
  taxonomies = {},
  contentCategoryCardDateFieldMapper = {},
}: {
  results: any[];
  page?: Br.Page;
  config: Record<string, string>;
  contentCategoryCardFieldMapper?: Record<string, string>;
  taxonomies?: Record<string, string>;
  contentCategoryCardDateFieldMapper?: Record<string, string>;
}) => {
  return results.map((item) => {
    const tags = item.tags?.raw || [];
    const contentCategoryValue = resolveESArray(item?.tax_content_category?.raw)?.[0] || '';
    const taxonomyIndex = Object.values(taxonomies).findIndex(
      (taxonomy: string) => taxonomy === contentCategoryValue,
    );
    // We shouldn't expect duplicated values with different keys.
    const contentCategoryKey = Object.keys(taxonomies)[taxonomyIndex] || '';
    const categoryField = contentCategoryCardFieldMapper?.[contentCategoryKey] || '';
    const dateValueField =
      contentCategoryCardDateFieldMapper?.[contentCategoryKey] || 'publication_date';

    return {
      description: item?.description?.snippet || item?.body?.snippet,
      title: item.title?.raw,
      image: page && getPageUrl({ url: item.image?.raw || item.icon?.raw || '', page }),
      category: resolveCategories({ raw: item, categoryType: categoryField }),
      // new
      contentCategory: contentCategoryValue,
      date: resolveDate({ rawVal: item?.[dateValueField]?.raw }),
      url: parseStringBool(item?.download?.raw)
        ? item?.url?.raw
        : page &&
          getPageUrl({
            url: parseUrl(item, config),
            page,
          }),
      categoryType: item?.[categoryField] ? categoryField : '',
      tags,
      icon: item.icon?.raw,
      resultID: item?.id?.raw,
      isPartnerOnly: parseStringBool(item?.is_partner_only?.raw),
      download: parseStringBool(item?.download?.raw),
    };
  });
};

export const SEARCH_RESULT_ELASTIC_DEFAULT_FIELDS = {
  custom: true,
  description: {
    snippet: {
      size: 200,
      fallback: true,
    },
  },
  title: {
    raw: {},
  },
  image: {
    raw: {},
  },
  publication_date: {
    raw: {},
  },
  release_date: {
    raw: {},
  },
  url: {
    raw: {},
  },
  podcast_series: {
    raw: {},
  },
  language: {
    raw: {},
  },
  body: {
    snippet: {
      size: 200,
      fallback: true,
    },
  },
  icon: {
    raw: {},
  },
  tax_topic: {
    raw: {},
  },
  tax_industry: {
    raw: {},
  },
  tax_content_category: {
    raw: {},
  },
};

export const elasticSearchAuthDefaultFields = {
  is_partner_only: {
    raw: {},
  },
  tax_language: {
    raw: {},
  },
  download: {
    raw: {},
  },
  category: {
    raw: {},
  },
};

const parseFilters = (filters: any) => pipe(omitBy(isNil))(filters);

const parseFacets = (filters: any) =>
  pipe(
    omitBy(isNil),
    keys,
    reduce<any, string[]>((result, keyValue) => [...result, keyValue], []),
  )(filters);

export const encodeWithCustomFilters = ({
  filters = {},
  facets = [],
  customsFilters = {},
}: any) => {
  const parsedFilters = parseFilters(filters);
  const parsedCustomFilters = parseFilters(customsFilters);
  const parsedCustomFacets = parseFacets(customsFilters);
  const encodedFilters = ElasticAdapter.encodeFilters(
    merge(parsedFilters, parsedCustomFilters),
    concat(facets, parsedCustomFacets),
  );

  return encodedFilters;
};

export const parsePartnerOnlyFilter = (filters: any) =>
  pipe(
    get('is_partner_only'),
    cond([
      [isArray, first],
      [T, identity],
    ]),
    (isPartnerOnlyValue) => ({ ...filters, is_partner_only: isPartnerOnlyValue }),
  )(filters);

export const partnerOnlyFacetsWithLanguageModifier = (filters: any, facets: string[]) =>
  filters?.is_partner_only === 'true' && !facets.includes('tax_language')
    ? [...facets, 'tax_language']
    : facets;
